import { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faCopy, faCheck, faHistory, faTrash } from "@fortawesome/free-solid-svg-icons";

export default function OpenMultipleUrls() {
    const [urlInput, setUrlInput] = useState("");
    const { darkMode } = useTheme();
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ message: "", visible: false });
    const [urlHistory, setUrlHistory] = useState([]);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tabBehavior, setTabBehavior] = useState("sequential"); // 'sequential' or 'all-at-once'
    const [delay, setDelay] = useState(500); // delay between opening tabs in ms

    // Load history from localStorage on component mount
    useEffect(() => {
        const savedHistory = localStorage.getItem("urlOpenerHistory");
        if (savedHistory) {
            setUrlHistory(JSON.parse(savedHistory));
        }
    }, []);

    // Save history to localStorage whenever it changes
    useEffect(() => {
        if (urlHistory.length > 0) {
            localStorage.setItem("urlOpenerHistory", JSON.stringify(urlHistory));
        }
    }, [urlHistory]);

    const showToast = (message, duration = 3000) => {
        setToast({ message, visible: true });
        setTimeout(() => {
            setToast({ message: "", visible: false });
        }, duration);
    };

    const normalizeUrl = (url) => {
        // Remove leading/trailing whitespace and add https:// if missing
        url = url.trim();
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
            return `https://${url}`;
        }
        return url;
    };

    const isValidUrl = (url) => {
        try {
            new URL(normalizeUrl(url));
            return true;
        } catch {
            return false;
        }
    };

    const openUrls = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        const urlArray = urlInput
            .split(/[\n,]+/)
            .map((url) => url.trim())
            .filter((url) => url);

        if (urlArray.length === 0) {
            setError("Please enter at least one valid URL");
            setLoading(false);
            return;
        }

        const validUrls = urlArray.filter(isValidUrl);

        if (validUrls.length === 0) {
            setError("No valid URLs found. Please check your input.");
            setLoading(false);
            return;
        }

        // Add to history if not already present
        const newHistory = [...new Set([...validUrls, ...urlHistory])].slice(0, 50);
        setUrlHistory(newHistory);

        try {
            if (tabBehavior === 'all-at-once') {
                // Open all URLs at once - this might be blocked by browsers
                validUrls.forEach(url => {
                    window.open(normalizeUrl(url), '_blank', 'noopener,noreferrer');
                });
            } else {
                // Open URLs sequentially with delay - better chance of working
                for (let i = 0; i < validUrls.length; i++) {
                    window.open(normalizeUrl(validUrls[i]), '_blank', 'noopener,noreferrer');
                    if (i < validUrls.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, delay));
                    }
                }
            }
            showToast(`${validUrls.length} URLs opened successfully!`);
        } catch (error) {
            console.error('Error opening URLs:', error);
            setError('Error opening some URLs. Please try again or check your browser popup settings.');
        } finally {
            setLoading(false);
        }
    };

    const clearAll = () => {
        setUrlInput("");
        setError(null);
    };

    const copyToClipboard = () => {
        if (urlInput.trim()) {
            navigator.clipboard.writeText(urlInput);
            setCopied(true);
            showToast("URLs copied to clipboard!");
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const loadFromHistory = (urls) => {
        setUrlInput(urls.join("\n"));
        showToast("URLs loaded from history");
    };

    const clearHistory = () => {
        setUrlHistory([]);
        localStorage.removeItem("urlOpenerHistory");
        showToast("History cleared");
    };

    return (
        <>
            <HeaderSection toolId="multiUrlOpener" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <div className="bg-white rounded-lg dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faEnvelope}
                                className="mr-3"
                            />
                            Multiple URL Opener Tool
                        </h1>
                        <p className="opacity-90">
                            Open multiple URLs in new tabs with comprehensive
                            validation checks
                        </p>
                    </div>

                    <div className="bg-white p-4 dark:bg-gray-800 rounded-xl p-6 mb-6">
                        <form onSubmit={openUrls}>
                            <div className="mb-6">
                                <div className="flex justify-between items-center mb-3">
                                    <label className="block text-gray-800 dark:text-gray-300 font-medium">
                                        URLs to Open
                                    </label>
                                    <div className="flex space-x-2">
                                        <button
                                            type="button"
                                            onClick={copyToClipboard}
                                            className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500 transition flex items-center"
                                            disabled={!urlInput.trim()}
                                        >
                                            <FontAwesomeIcon 
                                                icon={copied ? faCheck : faCopy} 
                                                className="mr-1" 
                                            />
                                            {copied ? "Copied!" : "Copy"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={clearAll}
                                            className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500 transition"
                                            disabled={!urlInput}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                                <textarea
                                    value={urlInput}
                                    onChange={(e) =>
                                        setUrlInput(e.target.value)
                                    }
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                                    placeholder="https://example.com&#10;https://another-site.com&#10;http://third-website.org"
                                    rows={8}
                                ></textarea>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                    Enter URLs separated by commas or new lines
                                </p>

                                {error && (
                                    <p
                                        className="text-red-500 dark:text-red-400 text-sm mt-2"
                                        role="alert"
                                    >
                                        {error}
                                    </p>
                                )}
                            </div>

                            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-800 dark:text-gray-300 font-medium mb-2">
                                        Opening Behavior
                                    </label>
                                    <div className="flex space-x-4">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="form-radio text-green-500"
                                                name="tabBehavior"
                                                value="sequential"
                                                checked={tabBehavior === "sequential"}
                                                onChange={() => setTabBehavior("sequential")}
                                            />
                                            <span className="ml-2 text-gray-700 dark:text-gray-300">Sequential</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                className="form-radio text-green-500"
                                                name="tabBehavior"
                                                value="all-at-once"
                                                checked={tabBehavior === "all-at-once"}
                                                onChange={() => setTabBehavior("all-at-once")}
                                            />
                                            <span className="ml-2 text-gray-700 dark:text-gray-300">All at once</span>
                                        </label>
                                    </div>
                                </div>

                                {tabBehavior === "sequential" && (
                                    <div>
                                        <label className="block text-gray-800 dark:text-gray-300 font-medium mb-2">
                                            Delay between tabs (ms)
                                        </label>
                                        <input
                                            type="number"
                                            min="100"
                                            max="5000"
                                            step="100"
                                            value={delay}
                                            onChange={(e) => setDelay(Math.min(Math.max(parseInt(e.target.value) || 500, 100), 5000))}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 px-4 bg-[#74D19C] hover:bg-[#74D19C]/80 text-white font-medium rounded-lg transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex justify-center items-center"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Opening URLs...
                                    </>
                                ) : (
                                    "Open URLs Now"
                                )}
                            </button>
                        </form>
                    </div>

                    {urlHistory.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6 mb-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                                    URL History
                                </h3>
                                <button
                                    onClick={clearHistory}
                                    className="text-xs px-3 py-1 bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-200 rounded-full hover:bg-red-200 dark:hover:bg-red-800 transition flex items-center"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                                    Clear History
                                </button>
                            </div>
                            <div className="space-y-2">
                                {urlHistory.slice(0, 5).map((url, index) => (
                                    <div 
                                        key={index} 
                                        className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                                    >
                                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                            {url}
                                        </span>
                                        <button
                                            onClick={() => loadFromHistory([url])}
                                            className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition flex items-center"
                                        >
                                            <FontAwesomeIcon icon={faHistory} className="mr-1" />
                                            Load
                                        </button>
                                    </div>
                                ))}
                                {urlHistory.length > 5 && (
                                    <button
                                        onClick={() => loadFromHistory(urlHistory)}
                                        className="w-full text-sm px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition flex items-center justify-center"
                                    >
                                        <FontAwesomeIcon icon={faHistory} className="mr-2" />
                                        Load All History ({urlHistory.length} URLs)
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                            How to use this tool
                        </h3>
                        <ol className="list-decimal pl-5 space-y-2 text-gray-700 dark:text-gray-300">
                            <li>
                                Enter one URL per line or separate with commas
                            </li>
                            <li>
                                Include http:// or https:// for best results (auto-added if missing)
                            </li>
                            <li>Choose opening behavior (sequential or all at once)</li>
                            <li>Click "Open URLs Now" to launch all sites</li>
                            <li>
                                Your browser may block popups - allow them for
                                this site
                            </li>
                        </ol>
                    </div>

                    {toast.visible && (
                        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg transition-opacity duration-300">
                            {toast.message}
                        </div>
                    )}
                </div>
            </div>
            <BottomSection toolId="multiUrlOpener" />
        </>
    );
}